import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { EchoesService } from '../services/echoesService';

const EchoesRating = ({ postId, postAuthorId, onRatingChange }) => {
  const { currentUser } = useAuth();
  const [isVisible, setIsVisible] = useState(true); // Toujours visible
  const [isPublishing, setIsPublishing] = useState(false);
  const [ratings, setRatings] = useState({
    intention: 3,
    composition: 3,
    matiere: 3,
    technique: 3,
    emotion: 3
  });
  const [allRatings, setAllRatings] = useState([]);
  const [averageRatings, setAverageRatings] = useState({
    intention: 0,
    composition: 0,
    matiere: 0,
    technique: 0,
    emotion: 0
  });

  // Vérifier si c'est son propre post
  const isOwnPost = currentUser && currentUser.uid === postAuthorId;

  // Charger la notation existante si elle existe (pour les posts des autres)
  // Ou charger toutes les notations (pour ses propres posts)
  useEffect(() => {
    if (currentUser && postId) {
      if (isOwnPost) {
        loadAllRatings();
      } else {
        loadExistingRating();
      }
    }
    
    // Afficher automatiquement le panneau de notation
    setIsVisible(true);
  }, [currentUser, postId, isOwnPost]);

  const loadExistingRating = async () => {
    try {
      const existingRating = await EchoesService.getRating(postId, currentUser.uid);
      if (existingRating) {
        setRatings(existingRating);
      }
    } catch (error) {
      console.error('Erreur chargement notation:', error);
    }
  };

  const loadAllRatings = async () => {
    try {
      const postRatings = await EchoesService.getPostRatings(postId);
      setAllRatings(postRatings);
      
      if (postRatings.length > 0) {
        const totals = {
          intention: 0,
          composition: 0,
          matiere: 0,
          technique: 0,
          emotion: 0
        };
        
        postRatings.forEach(rating => {
          totals.intention += rating.intention || 0;
          totals.composition += rating.composition || 0;
          totals.matiere += rating.matiere || 0;
          totals.technique += rating.technique || 0;
          totals.emotion += rating.emotion || 0;
        });
        
        const averages = {
          intention: (totals.intention / postRatings.length).toFixed(1),
          composition: (totals.composition / postRatings.length).toFixed(1),
          matiere: (totals.matiere / postRatings.length).toFixed(1),
          technique: (totals.technique / postRatings.length).toFixed(1),
          emotion: (totals.emotion / postRatings.length).toFixed(1)
        };
        
        setAverageRatings(averages);
      }
    } catch (error) {
      console.error('Erreur chargement notations du post:', error);
    }
  };

  const axes = [
    { key: 'intention', label: 'Intention' },
    { key: 'composition', label: 'Composition' },
    { key: 'matiere', label: 'Matière' },
    { key: 'technique', label: 'Technique' },
    { key: 'emotion', label: 'Émotion' }
  ];

  const updateRating = (axis, value) => {
    const newRatings = { ...ratings, [axis]: value };
    setRatings(newRatings);
  };

  const publishRating = async () => {
    if (!currentUser) {
      alert('Vous devez être connecté pour noter');
      return;
    }

    setIsPublishing(true);
    try {
      await EchoesService.saveRating(postId, currentUser.uid, ratings);
      if (onRatingChange) {
        // Notifier le parent que la publication a réussi (true = succès)
        onRatingChange(postId, ratings, true);
      }
      setIsVisible(false);
    } catch (error) {
      console.error('❌ Erreur publication notation:', error);
      alert('Erreur lors de la publication de votre notation');
    } finally {
      setIsPublishing(false);
    }
  };

  return React.createElement(React.Fragment, null,
    isVisible && React.createElement('div', {
      style: {
        position: 'relative',
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '12px',
        border: '1px solid #e5e5e5',
        maxHeight: '100%',
        overflow: 'auto',
        width: '100%'
      }
    },
    React.createElement('h3', { style: { margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#1f2937' } }, 'ECHOES'),
    React.createElement('p', { style: { fontSize: '14px', color: '#666', margin: '0 0 20px 0' } }, 
      isOwnPost ? 'Vos notes reçues' : '5 axes artistiques pour évaluer cette création'),
      
    isOwnPost ? (
    // Afficher les statistiques pour ses propres posts
      React.createElement(React.Fragment, null,
        React.createElement('div', { 
          key: 'stats',
          style: { 
            marginBottom: '20px',
            textAlign: 'center',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px'
          }
        },
        React.createElement('div', { style: { fontSize: '24px', fontWeight: '600', color: '#1f2937' } }, allRatings.length),
        React.createElement('div', { style: { fontSize: '14px', color: '#6b7280' } }, 
          `notation${allRatings.length !== 1 ? 's' : ''} reçue${allRatings.length !== 1 ? 's' : ''}`)
        ),
          
        allRatings.length > 0 ? React.createElement('div', { key: 'averages' },
          React.createElement('h4', { style: { margin: '0 0 16px 0', fontSize: '16px', color: '#374151' } }, 'Moyennes par axe :'),
          ...axes.map((axis) =>
            React.createElement('div', { 
              key: axis.key, 
              style: { 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '12px',
                padding: '8px 0'
              } 
            },
            React.createElement('span', { style: { fontSize: '14px', fontWeight: '500', color: '#374151' } }, axis.label),
            React.createElement('span', { style: { fontSize: '16px', color: '#f59e0b', fontWeight: '600' } }, 
              `${averageRatings[axis.key]}/5`)
            )
          )
        ) : React.createElement('div', { 
          key: 'no-ratings',
          style: { 
            textAlign: 'center', 
            padding: '20px',
            color: '#6b7280' 
          } 
        }, 'Aucune notation reçue pour cette publication')
      )
    ) : (
    // Afficher le système de notation pour les posts des autres
      React.createElement(React.Fragment, null,
        ...axes.map((axis) =>
          React.createElement('div', { key: axis.key, style: { marginBottom: '16px' } },
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' } },
              React.createElement('span', { style: { fontSize: '14px', fontWeight: '500', color: '#374151' } }, axis.label),
              React.createElement('span', { style: { fontSize: '16px', color: '#f59e0b', fontWeight: '600' } }, ratings[axis.key])
            ),
            React.createElement('input', {
              type: 'range',
              min: '1',
              max: '5',
              value: ratings[axis.key],
              onChange: (e) => updateRating(axis.key, parseInt(e.target.value)),
              style: { 
                width: '100%', 
                height: '6px', 
                cursor: 'pointer',
                background: '#e5e7eb',
                borderRadius: '3px',
                outline: 'none',
                WebkitAppearance: 'none',
                appearance: 'none'
              }
            })
          )
        ),
        React.createElement('button', {
          onClick: publishRating,
          disabled: isPublishing,
          style: {
            backgroundColor: isPublishing ? '#9ca3af' : '#f59e0b',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: isPublishing ? 'not-allowed' : 'pointer',
            marginTop: '16px',
            width: '100%',
            transition: 'all 0.2s ease'
          }
        }, isPublishing ? 'Publication...' : 'Publier ma notation ECHOES')
      )
    ),
      
    // Bouton de publication supprimé pour éviter la duplication, on garde uniquement celui dans le formulaire
    )
  );
};

export default EchoesRating;
